import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import AmbulanceIcon from '../../assets/icons/Ambulance'
import moment from 'moment'

export const getStatusColor = (x) => {
  const status = x.toLowerCase()
  switch (status) {
    case 'new':
      return 'anRed'
    case 'in progress':
      return 'anOrange'
    case 'completed':
    case 'quote sent':
      return 'anGreen'
    case 'potential':
    case 'waiting':
      return 'anPurple'
    case 'interested':
      return 'anBlue'
    case 'duplicate - closed':
      return 'secondary'
    default:
      return 'primary'
  }
}
const KanbanTab = ({ selectedStatuses, items, urlPrefix }) => {
  const navigate = useNavigate()

  if (selectedStatuses.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'anMuted.main',
        }}
      >
        <Typography variant="title3">No statuses selected</Typography>
        <Typography variant="body2">
          Choose one or more statuses to populate the board.
        </Typography>
      </Paper>
    )
  }

  return (
    <Box
      sx={{
        overflowX: { xs: 'visible', lg: 'auto' },
        pb: 0.5,
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        gap={1.5}
        alignItems="stretch"
        sx={{
          minWidth: {
            lg: selectedStatuses.length > 3 ? selectedStatuses.length * 300 : 0,
          },
        }}
      >
        {selectedStatuses.map((status) => {
          const statusItems = items.filter((x) => x.status === status)
          const statusColor = getStatusColor(status)

          return (
            <Paper
              key={status}
              elevation={0}
              sx={(theme) => ({
                flex: 1,
                minWidth: { lg: 280 },
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(
                  theme.palette[statusColor]?.main ??
                    theme.palette.primary.main,
                  0.06,
                ),
                overflow: 'hidden',
              })}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: `${statusColor}.main`,
                    }}
                  />
                  <Typography variant="title" noWrap>
                    {status}
                  </Typography>
                </Stack>
                <Chip
                  label={statusItems.length}
                  size="small"
                  color={statusColor}
                  sx={{ minWidth: 34 }}
                />
              </Stack>

              <Stack
                padding={1}
                gap={1}
                maxHeight={{ lg: '68vh' }}
                minHeight={140}
                height="100%"
                overflow="auto"
              >
                {statusItems.length === 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                      borderStyle: 'dashed',
                    }}
                  >
                    <Typography variant="body2">Nothing here</Typography>
                  </Paper>
                )}

                {statusItems.map((item) => {
                  const title = item.summary ?? item.title

                  return (
                    <Paper
                      key={item.id}
                      elevation={0}
                      onClick={() =>
                        navigate(urlPrefix ? urlPrefix + item.id : item.id)
                      }
                      sx={{
                        p: 1.25,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeft: '4px solid',
                        borderLeftColor: `${statusColor}.main`,
                        bgcolor: 'background.paper',
                        transition:
                          'border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
                        '&:hover': {
                          borderColor: `${statusColor}.main`,
                          boxShadow: '0 8px 18px rgba(15, 23, 42, 0.10)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Stack gap={1}>
                        <Stack
                          direction="row"
                          gap={1}
                          justifyContent="space-between"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.primary',
                              fontWeight: 600,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {title}
                          </Typography>
                          {item?.defectNumber && (
                            <Tooltip title='Defect Reference Number'><Chip
                              label={item.defectNumber}
                              color="contrast"
                              size="small"
                            />
                            </Tooltip>
                          )}
                        </Stack>

                        {item?.organisation?.orgName && (
                          <Typography variant="caption" color="text.secondary">
                            {item.organisation.orgName}
                          </Typography>
                        )}

                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                          {item.type && (
                            <Chip
                              label={item.type}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {item.vehicle && (
                            <Chip
                              icon={<AmbulanceIcon />}
                              label={item.vehicle.callSign}
                              color="anMainBlue"
                              size="small"
                              sx={{ pl: 0.75 }}
                            />
                          )}
                          {item.asset && (
                            <Chip
                              label={item.asset.assetID ?? item.asset}
                              color="anMainBlue"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {item.isIPC && (
                            <Chip label="IPC" color="info" size="small" />
                          )}
                          {item.startDate && (
                            <Chip
                              label={moment(item.startDate)
                                .format('DD/MM/YYYY')
                                .toString()}
                              color="anMainBlue"
                              size="small"
                            />
                          )}
                          {item.date && (
                            <Chip
                              label={moment(item.date)
                                .format('DD/MM/YYYY')
                                .toString()}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
            </Paper>
          )
        })}
      </Stack>
    </Box>
  )
}

export default KanbanTab
